import React, { useEffect, useState } from "react";
import { getCategories } from "../../api/categoryService";
import { getBlogs } from "../../api/BlogCategoryService";
import { getStaffListApi } from "../../api/staffService";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const defaultValues = {
  title: "",
  slug: "",
  description: "",
  category: "",        
  productCategory: "",
  author: "",
  readingTime: "",
  metaTitle: "",
  metaDescription: "",
  metaKeywords: "",
  content: "",
  status: "ACTIVE",
  coverImage: null,
};

const BlogForm = ({
  blog,
  initialValues,
  onSubmit,
  loading,
  onCancel,
  errors = {},
}) => {
  const [productCategories, setProductCategories] = useState([]);
  const [blogCategories, setBlogCategories] = useState([]);
  const [authors, setAuthors] = useState([]);

  const [formData, setFormData] = useState({
    ...defaultValues,
    ...(initialValues || {}),
  });

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    setFormData({
      ...defaultValues,
      ...(initialValues || {}),
    });
  }, [initialValues]);

  const loadCategories = async () => {
    try {
      const [productRes, blogRes, staffRes] = await Promise.all([
        getCategories(),
        getBlogs(),
        getStaffListApi(),
      ]);

      setProductCategories(productRes?.data || []);
      setBlogCategories(blogRes?.data || []);

      setAuthors(staffRes?.data || staffRes?.staff || staffRes?.users || []);
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files && files.length > 0) {
      const file = files[0];

      const reader = new FileReader();

      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          [name]: reader.result, // Base64 string
        }));
      };

      reader.readAsDataURL(file);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (onSubmit) {
      onSubmit(formData);
    }
  };

  return (
    <div className="container-fluid p-4">
      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <h4 className="card-title mb-4">
              {blog?._id ? "Edit Blog" : "Add Blog"}
            </h4>

            <div className="row">
              {/* Title */}
              <div className="col-md-12 mb-3 h80">
                <label className="form-label">
                  Blog Title <span className="text-danger">*</span>
                </label>

                <input
                  type="text"
                  name="title"
                  className={`form-control ${errors.title ? "is-invalid" : ""}`}
                  value={formData.title}
                  onChange={handleChange}
                />

                {errors.title && (
                  <div className="invalid-feedback">{errors.title}</div>
                )}
              </div>

              {/* Description */}
              <div className="col-md-12 mb-4">
                <label className="form-label">Blog Description</label>

                <ReactQuill
                  theme="snow"
                  value={formData.content || ""}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      content: value,
                    }))
                  }
                />
              </div>

              {/* Blog Category */}
              <div className="col-md-6 mb-3 h80">
                <label className="form-label">
                  Blog Category<span className="text-danger">*</span>
                </label>

                <select
                  className={`form-select ${errors.title ? "is-invalid" : ""}`}
                  name="category"
                  value={formData.category || ""}
                  onChange={handleChange}
                >
                  <option value="">Select Blog Category</option>

                  {blogCategories.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <div className="invalid-feedback">{errors.category}</div>
                )}
              </div>

              {/* Product Category */}
              <div className="col-md-6 mb-3 h80">
                <label className="form-label">Product Category</label>

                <select
                  className="form-select"
                  name="productCategory"
                  value={formData.productCategory}
                  onChange={handleChange}
                >
                  <option value="">Select Product Category</option>

                  {productCategories.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Author */}
              <div className="col-md-6 mb-3 h80">
                <label className="form-label">
                  Author<span className="text-danger">*</span>
                </label>

                <select
                  name="author"
                  className={`form-select ${errors.author ? "is-invalid" : ""}`}
                  value={formData.author || ""}
                  onChange={handleChange}
                >
                  <option value="">Select Author</option>

                  {authors.map((author) => (
                    <option key={author._id} value={author._id}>
                      {author._id}
                    </option>
                  ))}
                </select>

                {errors.author && (
                  <div className="invalid-feedback">{errors.author}</div>
                )}
              </div>

              {/* Reading Time */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Reading Time</label>

                <input
                  type="text"
                  name="readingTime"
                  className="form-control"
                  value={formData.readingTime}
                  onChange={handleChange}
                />
              </div>

              {/* SEO Title */}
              <div className="col-md-12 mb-3">
                <label className="form-label">SEO Title</label>

                <input
                  type="text"
                  name="metaTitle"
                  className="form-control"
                  value={formData.metaTitle}
                  onChange={handleChange}
                />
              </div>

              {/* SEO Description */}
              <div className="col-md-12 mb-3">
                <label className="form-label">SEO Description</label>

                <textarea
                  name="metaDescription"
                  className="form-control"
                  value={formData.metaDescription}
                  onChange={handleChange}
                />
              </div>

              {/* Keywords */}
              <div className="col-md-12 mb-3">
                <label className="form-label">SEO Keywords</label>

                <input
                  type="text"
                  name="metaKeywords"
                  className="form-control"
                  value={formData.metaKeywords}
                  onChange={handleChange}
                />
              </div>

              {/* Content */}
              <div className="col-md-12 mb-3">
                <label className="form-label">Content</label>

                <textarea
                  name="content"
                  className="form-control"
                  rows="5"
                  value={formData.content}
                  onChange={handleChange}
                />
              </div>

              {/* Status */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Status</label>

                <select
                  name="status"
                  className="form-select"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="ACTIVE">Active</option>

                  <option value="INACTIVE">Inactive</option>
                </select>
              </div>

              {/* Image */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Cover Image</label>

                <input
                  type="file"
                  name="coverImage"
                  className="form-control"
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="text-end">
              <button
                type="submit"
                className="btn btn-success me-2"
                disabled={loading}
              >
                {loading ? "Saving..." : blog?._id ? "Update" : "Submit"}
              </button>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BlogForm;
